import { use, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPatch } from "@softmax/observatory-client/client";
import { ApiError } from "@softmax/observatory-client/transport";
import type { Schemas } from "@softmax/observatory-client/types";
import {
  QueryViewerContext,
  invalidateResourceQueries,
} from "@softmax/web-features/data";
import { isUncertainWrite } from "../write-failure";

type LeagueSeed = Schemas["CoworldLeagueSeedPublic"];

export function useLeagueSettings(leagueId: string) {
  const viewerId = use(QueryViewerContext);
  const client = useQueryClient();
  const queryKey = ["league-settings", viewerId, leagueId];
  const submitting = useRef(false);
  const seed = useQuery({
    queryKey,
    queryFn: ({ signal }) =>
      apiGet<LeagueSeed>(
        `/v2/coworld-league-seeds/by-league/${encodeURIComponent(leagueId)}`,
        { elevatedPrivileges: false, signal },
      ),
    retry: false,
  });
  const [draft, setDraft] = useState<string | null>(null);
  const rename = useMutation({
    retry: false,
    onMutate: () => client.cancelQueries({ queryKey, exact: true }),
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      apiPatch<LeagueSeed>(
        `/v2/coworld-league-seeds/${encodeURIComponent(id)}`,
        { league_name: name },
        { elevatedPrivileges: false },
      ),
    onSuccess: async (response) => {
      await client.cancelQueries({ queryKey, exact: true });
      client.setQueryData<LeagueSeed>(queryKey, (current) =>
        current === undefined ? undefined : response,
      );
      void invalidateResourceQueries({
        client,
        queryKeys: [queryKey, ["league-directory"], ["leagues", leagueId]],
      });
    },
    onSettled: () => {
      submitting.current = false;
    },
  });
  const uncertain = Boolean(rename.error && isUncertainWrite(rename.error));
  const denied =
    seed.error instanceof ApiError &&
    [401, 403, 404].includes(seed.error.status);
  const name = draft ?? seed.data?.league_name ?? "";
  const refresh = async () => {
    if (seed.isFetching) return;

    const result = await seed.refetch();

    if (result.isSuccess && uncertain) rename.reset();
  };
  function changeName(value: string) {
    setDraft(value);

    if (!uncertain) rename.reset();
  }
  function cancelName() {
    setDraft(null);

    if (!uncertain) rename.reset();
  }
  function saveName(id: string) {
    if (submitting.current || uncertain || seed.error) return;

    submitting.current = true;
    rename.mutate(
      { id, name: name.trim() },
      { onSuccess: () => setDraft(null) },
    );
  }
  return {
    seed,
    draft,
    rename,
    uncertain,
    denied,
    name,
    refresh,
    changeName,
    cancelName,
    saveName,
  };
}
